import { queryOptions } from '@tanstack/react-query';

import { FIVE_MINUTES } from '@/constants/time';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type GetMentorsV2Data = NonNullable<
  paths['/v2/mentors']['get']['responses'][200]['content']['application/json']['data']
>;

export type MentorV2 = GetMentorsV2Data['mentors'][number];

export type GetMentorsV2Query = NonNullable<paths['/v2/mentors']['get']['parameters']['query']>;

/**
 * The mentorship roster. Unlike `searchUsersV2`, this endpoint is readable by
 * any confirmed account, so it is what the Fellows Corner page can call.
 *
 * Membership is the backend's curated mentor id list, not a specialty filter,
 * and the order is the configured roster order rather than alphabetical, so
 * the response is rendered as-is. Returns identities only, hydrated for
 * display through `getUserPublicV2`.
 */
export async function getMentorsV2({ page = 1, limit = 25 }: GetMentorsV2Query = {}): Promise<
  GetMentorsV2Data | undefined
> {
  const { data } = await fetchClient.GET('/v2/mentors', {
    params: { query: { page, limit } },
  });

  return data?.data;
}

export function getMentorsV2QueryOptions({ page = 1, limit = 25 }: GetMentorsV2Query = {}) {
  return queryOptions<GetMentorsV2Data | undefined>({
    queryKey: ['getMentorsV2', { page, limit }],
    queryFn: () => getMentorsV2({ page, limit }),
    // The roster turns over on the order of months, and this is the one request
    // the lineup waits on, so refetching it per mount or window focus buys
    // nothing and costs the page its first paint.
    staleTime: FIVE_MINUTES,
    // No retry: a missing/unavailable route should fail straight to the
    // page's error state rather than keep the loading spinner up through
    // several rounds of backoff first.
    retry: false,
  });
}
